import { getCountries, getCountryCallingCode, parsePhoneNumberFromString } from 'libphonenumber-js';

export const DEFAULT_PHONE_COUNTRY = 'LK';

const regionDisplayNames =
  typeof Intl !== 'undefined' && typeof Intl.DisplayNames === 'function'
    ? new Intl.DisplayNames(['en'], { type: 'region' })
    : null;

const COUNTRY_NAME_OVERRIDES = {
  AC: 'Ascension Island',
  TA: 'Tristan da Cunha',
  XK: 'Kosovo'
};

const getCountryName = (isoCode) => {
  if (COUNTRY_NAME_OVERRIDES[isoCode]) {
    return COUNTRY_NAME_OVERRIDES[isoCode];
  }

  return regionDisplayNames?.of(isoCode) || isoCode;
};

export const PHONE_COUNTRY_OPTIONS = getCountries()
  .map((isoCode) => {
    const dialCode = getCountryCallingCode(isoCode);
    const name = getCountryName(isoCode);

    return {
      isoCode,
      dialCode,
      name,
      label: `${name} (+${dialCode})`,
      searchText: `${name} ${isoCode} ${dialCode}`.toLowerCase()
    };
  })
  .sort((left, right) => left.name.localeCompare(right.name));

export const findPhoneCountryOption = (isoCode) => {
  return PHONE_COUNTRY_OPTIONS.find((option) => option.isoCode === isoCode) || PHONE_COUNTRY_OPTIONS.find((option) => option.isoCode === DEFAULT_PHONE_COUNTRY);
};

export const getPhoneDigits = (value) => (value || '').toString().replace(/\D/g, '');

const getSanitizedPhoneInput = (value) => (value || '').toString().trim().replace(/(?!^)\+/g, '').replace(/[^\d+\s().-]/g, '');

const tryParsePhone = (value, country) => {
  if (!value) {
    return null;
  }

  try {
    return parsePhoneNumberFromString(value, country);
  } catch (error) {
    return null;
  }
};

const getDisplayNationalNumber = ({ rawValue, digits, parsed }) => {
  if (!parsed) {
    return rawValue;
  }

  // Keep legacy local values visually unchanged so existing records continue
  // to look familiar until a user updates them through the new UI.
  if (digits.startsWith('0')) {
    return rawValue;
  }

  // For normalized international values, show the national number without
  // reintroducing the local trunk prefix after reload.
  return parsed.nationalNumber || '';
};

export const parseStoredPhoneValue = (value, defaultCountry = DEFAULT_PHONE_COUNTRY) => {
  const rawValue = getSanitizedPhoneInput(value);

  if (!rawValue) {
    return {
      rawValue: '',
      country: defaultCountry,
      nationalNumber: '',
      displayNationalNumber: '',
      normalizedValue: '',
      isValid: false,
      isLegacy: false
    };
  }

  const digits = getPhoneDigits(rawValue);
  const parseCandidates = [];

  if (rawValue.startsWith('+')) {
    parseCandidates.push({ value: rawValue, country: undefined });
  }

  if (digits && !rawValue.startsWith('0')) {
    parseCandidates.push({ value: `+${digits}`, country: undefined });
  }

  if (defaultCountry) {
    parseCandidates.push({ value: rawValue, country: defaultCountry });
    if (digits) {
      parseCandidates.push({ value: digits, country: defaultCountry });
    }
  }

  for (const candidate of parseCandidates) {
    const parsed = tryParsePhone(candidate.value, candidate.country);
    if (parsed?.isValid()) {
      return {
        rawValue,
        country: parsed.country || candidate.country || defaultCountry,
        nationalNumber: parsed.nationalNumber || '',
        displayNationalNumber: getDisplayNationalNumber({ rawValue, digits, parsed }),
        normalizedValue: getPhoneDigits(parsed.number),
        isValid: true,
        isLegacy: digits.startsWith('0')
      };
    }
  }

  return {
    rawValue,
    country: defaultCountry,
    nationalNumber: digits,
    displayNationalNumber: rawValue,
    normalizedValue: digits,
    isValid: false,
    isLegacy: digits.startsWith('0')
  };
};

export const buildPhoneStorageValue = ({ country = DEFAULT_PHONE_COUNTRY, nationalNumber }) => {
  const rawNationalNumber = (nationalNumber || '').toString().trim();

  if (!rawNationalNumber) {
    return {
      value: '',
      isValid: false,
      parsedPhone: null
    };
  }

  const parsed = tryParsePhone(rawNationalNumber, country);
  if (parsed?.isValid()) {
    return {
      value: getPhoneDigits(parsed.number),
      isValid: true,
      parsedPhone: parsed
    };
  }

  const digits = getPhoneDigits(rawNationalNumber);
  if (!digits) {
    return {
      value: '',
      isValid: false,
      parsedPhone: null
    };
  }

  const dialCode = getCountryCallingCode(country);
  return {
    value: `${dialCode}${digits.replace(/^0+/, '')}`,
    isValid: false,
    parsedPhone: null
  };
};

export const validateStoredPhoneValue = (value, defaultCountry = DEFAULT_PHONE_COUNTRY, { required = false } = {}) => {
  const parsed = parseStoredPhoneValue(value, defaultCountry);

  if (!parsed.rawValue) {
    return {
      isValid: !required,
      normalizedValue: '',
      parsed
    };
  }

  return {
    isValid: parsed.isValid,
    normalizedValue: parsed.isValid ? parsed.normalizedValue : parsed.rawValue,
    parsed
  };
};

/**
 * Checks whether a candidate phone number matches a search query across common
 * formatting variations (local 0-prefix, international 94-prefix, raw digits, spaces, hyphens).
 *
 * @param {string} candidate
 * @param {string} search
 * @returns {boolean}
 */
export const matchesPhoneSearch = (candidate, search) => {
  if (!candidate || !search) return false;
  const rawCand = String(candidate).trim();
  const rawSearch = String(search).trim();
  if (!rawCand || !rawSearch) return false;

  // Direct substring check
  if (rawCand.toLowerCase().includes(rawSearch.toLowerCase())) {
    return true;
  }

  const candDigits = rawCand.replace(/\D/g, '');
  const searchDigits = rawSearch.replace(/\D/g, '');

  if (!searchDigits || searchDigits.length < 3) {
    return false;
  }

  if (candDigits.includes(searchDigits)) {
    return true;
  }

  // Handle Sri Lankan phone prefix variations
  const searchVariants = new Set([searchDigits]);
  if (searchDigits.startsWith('0') && searchDigits.length >= 4) {
    const withoutZero = searchDigits.slice(1);
    searchVariants.add(withoutZero);
    searchVariants.add(`94${withoutZero}`);
  } else if (searchDigits.startsWith('94') && searchDigits.length >= 5) {
    const without94 = searchDigits.slice(2);
    searchVariants.add(without94);
    searchVariants.add(`0${without94}`);
  } else if (searchDigits.length >= 7 && !searchDigits.startsWith('0') && !searchDigits.startsWith('94')) {
    searchVariants.add(`0${searchDigits}`);
    searchVariants.add(`94${searchDigits}`);
  }

  for (const variant of searchVariants) {
    if (candDigits.includes(variant)) {
      return true;
    }
  }

  return false;
};